import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { ArrowRight, ShieldCheck, Loader2 } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user === undefined) return null;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#222222] text-[#FAFAFA] flex items-center justify-center px-6 py-16 relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: "linear-gradient(rgba(250,250,250,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(250,250,250,0.5) 1px, transparent 1px)",
        backgroundSize: "80px 80px",
      }} aria-hidden />
      <div className="absolute -right-40 -top-40 w-[520px] h-[520px] rounded-full bg-[#A4161A]/15 blur-3xl" aria-hidden />

      <div className="relative w-full max-w-md">
        <div className="mb-10">
          <a href="/" className="inline-flex items-center gap-3 text-[#FAFAFA] text-[15px]" data-testid="admin-login-home-link">
            <img src="/brand/logo.png" alt="DB Computronics" className="w-10 h-10 rounded-md object-contain" />
            <span className="font-display tracking-tight">Computronics<span className="text-[#A4161A]">.</span></span>
          </a>
        </div>

        <div className="section-label mb-4">Admin Console · v1</div>
        <h1 className="font-display text-[36px] sm:text-[44px] leading-[1.02] tracking-[-0.03em] mb-3">
          Sign in to<br /><span className="text-[#A4161A]">manage quotes</span>.
        </h1>
        <p className="text-[#9A9A9A] font-body text-[14px] mb-10 max-w-[42ch]">
          Restricted access. Only authorised DB Computronics operators may view submissions.
        </p>

        <form onSubmit={submit} className="bg-[#FAFAFA] text-[#222222] rounded-xl p-8 border border-[#9A9A9A]/20" data-testid="admin-login-form" noValidate>
          <label className="text-[10px] tracking-[0.28em] uppercase text-[#9A9A9A] font-btn">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-field mb-6"
            placeholder="admin@dbcomputronics.in"
            autoComplete="email"
            data-testid="admin-email-input"
          />
          <label className="text-[10px] tracking-[0.28em] uppercase text-[#9A9A9A] font-btn">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-field mb-6"
            placeholder="••••••••"
            autoComplete="current-password"
            data-testid="admin-password-input"
          />

          {err && (
            <div className="text-[#A4161A] text-[13px] font-body mb-6" data-testid="admin-login-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="btn-red inline-flex items-center gap-3 px-6 py-4 rounded-lg font-btn text-[12px] tracking-[0.22em] uppercase w-full justify-center disabled:opacity-70"
            data-testid="admin-login-submit"
          >
            {busy ? (
              <>
                <Loader2 size={16} className="animate-spin relative z-10" />
                <span>Signing in…</span>
              </>
            ) : (
              <>
                <ShieldCheck size={16} className="relative z-10" />
                <span>Sign In Securely</span>
                <ArrowRight size={16} className="relative z-10" />
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-[11px] tracking-[0.22em] uppercase text-[#9A9A9A] font-btn">
          <a href="/" className="red-underline" data-testid="admin-back-to-site">← Back to site</a>
        </div>
      </div>
    </div>
  );
}
